/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any
 * JavaScript running in the front-end, then you should delete this file and remove
 * the `viewScript` property from `block.json`.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

import { parallaxImages } from "../common/animations/parrallaxImages.js";

function ensureYoutubeAPIReady() {
	return new Promise((resolve, reject) => {
		if (window.YT && window.YT.Player) {
			return resolve();
		}

		let otherYoutubeReadyCb = null;
		if (typeof window.onYouTubeIframeAPIReady !== "undefined") {
			otherYoutubeReadyCb = window.onYouTubeIframeAPIReady;
		}

		window.onYouTubeIframeAPIReady = () => {
			otherYoutubeReadyCb && otherYoutubeReadyCb();
			resolve();
		};

		let script = document.createElement("script");
		script.id = "youtube-iframe-script";
		script.src = "https://www.youtube.com/iframe_api";
		let firstScriptTag = document.getElementsByTagName("script")[0];
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	});
}

function ensureVimeoAPIReady() {
	return new Promise((resolve, reject) => {
		if (window.Vimeo && window.Vimeo.Player) {
			return resolve();
		}

		let script = document.createElement("script");
		script.id = "vimeo-iframe-script";
		script.src = "https://player.vimeo.com/api/player.js";

		// Set up onload event handler
		script.onload = () => {
			// Check if the necessary objects are now available
			if (window.Vimeo && window.Vimeo.Player) {
				resolve();
			} else {
				reject(
					new Error(
						"Vimeo API script loaded, but Vimeo or Vimeo.Player is still not available",
					),
				);
			}
		};

		// Set up onerror event handler
		script.onerror = () => {
			reject(new Error("Error loading Vimeo API script"));
		};

		let firstScriptTag = document.getElementsByTagName("script")[0];
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	});
}

const removePosterAndOverlay = (video) => {
	const poster = video.querySelector(
		".staggered-media-and-content-video__poster",
	);
	const overlay = video.querySelector(
		".staggered-media-and-content-video__overlay",
	);

	if (poster) {
		poster.style.opacity = 0;
		poster.style.pointerEvents = "none";
	}

	if (overlay) {
		overlay.style.opacity = 0;
		overlay.style.pointerEvents = "none";
	}
};

const enterKeyPressPlay = (video, play) => {
	const playButton = video.querySelector(
		".staggered-media-and-content-video__overlay-icon",
	);
	if (playButton) {
		playButton.addEventListener("keydown", (e) => {
			if (e.key === "Enter") {
				removePosterAndOverlay(video);
				play();
			}
		});
	}
};

const handleIframeVideos = () => {
	const videoCollection = document.getElementsByClassName(
		"staggered-media-and-content-video__stacking-grid",
	);

	if (videoCollection) {
		const videoArray = Array.from(videoCollection);

		ensureYoutubeAPIReady().then(() => {
			for (let i = 0; i < videoArray.length; i++) {
				const videoItem = videoArray[i];
				// Check if the current videoItem has the class 'source-video-youtube'
				if (videoItem.dataset.videoType === "video-youtube") {
					// If the class is found, run the function and stop the loop
					videoArray.forEach((video, i) => {
						// events if youtube video is loaded
						const frame = video.getElementsByTagName("iframe");
						if (frame.length) {
							if (video.dataset.videoType === "video-youtube") {
								frame[0].style.pointerEvents = "none";

								let playAfterReady = false;
								let isPlayerReady = false;
								const player = new YT.Player(frame[0], {
									events: {
										onReady: () => {
											isPlayerReady = true;
											if (playAfterReady) {
												player.playVideo();
											}
										},
										onStateChange: (e) => {
											if (e.target.getPlayerState() === 1) {
												frame[0].style.visibility = "visible";
												frame[0].style.pointerEvents = "auto";
												removePosterAndOverlay(video);
											}
											if (
												video.dataset.loop === "true" &&
												e.target.getPlayerState() === 0
											) {
												player.playVideo();
											}
										},
									},
								});

								function handlePlay() {
									if (!isPlayerReady) {
										playAfterReady = true;
									} else {
										player.playVideo();
									}
								}

								enterKeyPressPlay(video, handlePlay);
								if (video.dataset.autoplay === "true") {
									handlePlay();
								}
								video.addEventListener("click", () => {
									handlePlay();
								});
							}
						}
					});
					break;
				}
			}
		});

		for (let i = 0; i < videoArray.length; i++) {
			const videoItem = videoArray[i];
			// Check if the current videoItem has the class 'source-video-vimeo'
			if (videoItem.dataset.videoType === "video-vimeo") {
				// If the class is found, run the function and stop the loop
				ensureVimeoAPIReady().then(() => {
					videoArray.forEach((video, i) => {
						// events if Vimeo video is loaded
						const frame = video.getElementsByTagName("iframe");

						if (frame.length) {
							if (video.dataset.videoType === "video-vimeo") {
								frame[0].onload = function () {
									ensureVimeoAPIReady().then(() => {
										const player = new Vimeo.Player(frame[0]);
										const play = () => {
											player.play();
										};

										const poster = video.querySelector(
											".staggered-media-and-content-video__poster",
										);
										const overlay = video.querySelector(
											".staggered-media-and-content-video__overlay",
										);

										if (poster) {
											poster.addEventListener("click", () => {
												play();
											});
										}

										if (overlay) {
											overlay.addEventListener("click", () => {
												play();
											});
										}

										enterKeyPressPlay(video, play);

										player.on("play", () => {
											removePosterAndOverlay(video);
										});
									});
								};
							}
						}
					});
				});
				break;
			}
		}
	}
};

const handleLocalVideos = () => {
	const fullWidthVideoBlocks = document.querySelectorAll(
		".wp-block-block-builder-by-arc-staggered-media-and-content",
	);

	fullWidthVideoBlocks.forEach((block) => {
		const video = block.querySelector(
			".staggered-media-and-content__local-video",
		);
		const playButton = block.querySelector(
			".staggered-media-and-content-video__overlay",
		);
		const stackingGrid = block.querySelector(
			".staggered-media-and-content-video__stacking-grid",
		);

		if (video) {
			const play = () => {
				video.play();
			};

			const poster = block.querySelector(
				".staggered-media-and-content-video__poster",
			);
			const overlay = block.querySelector(
				".staggered-media-and-content-video__overlay",
			);

			if (poster) {
				if (stackingGrid.dataset.controls === "true") {
					video.controls = false;
				}

				poster.addEventListener("click", () => {
					if (stackingGrid.dataset.controls === "true") {
						video.controls = true;
					}
					play();
				});
			}

			if (overlay) {
				overlay.addEventListener("click", () => {
					if (stackingGrid.dataset.controls === "true") {
						video.controls = true;
					}
					play();
				});
			}

			if (playButton) {
				playButton.addEventListener("keydown", (e) => {
					if (e.key === "Enter") {
						if (stackingGrid.dataset.controls === "true") {
							video.controls = true;
						}
						removePosterAndOverlay(video);
						play();
					}
				});
			}

			video.addEventListener("playing", function () {
				removePosterAndOverlay(block);
			});
		}
	});
};

// Position caption absolutely at bottom of image using Y position
const positionCaptions = () => {
	const imageContainers = document.querySelectorAll(
		".staggered-media-and-content__image",
	);

	const TABLET_MAX = 1025; // Match $tablet-max breakpoint

	const updateCaptionPosition = (imageContainer, isInitial = false) => {
		// Find caption as child of image container
		const caption = imageContainer.querySelector(
			".staggered-media-and-content__caption",
		);
		const img = imageContainer.querySelector("img");

		if (caption && img) {
			const isMobile = window.innerWidth < TABLET_MAX;

			if (isMobile) {
				// For mobile/tablet: move caption outside image container to avoid overflow hidden
				const columnOne = imageContainer.closest(
					".staggered-media-and-content__column-one",
				);
				if (!columnOne) return;

				// Check if caption is already moved outside
				if (caption.parentElement === imageContainer) {
					// Move caption to be sibling of image container
					imageContainer.parentNode.insertBefore(caption, imageContainer.nextSibling);
				}

				// Set column-one to relative positioning if not already
				columnOne.style.position = "relative";

				// Calculate image container bottom position relative to column-one
				const imageContainerRect = imageContainer.getBoundingClientRect();
				const columnRect = columnOne.getBoundingClientRect();
				const imageContainerBottom = imageContainerRect.bottom - columnRect.top;
				
				caption.style.position = "absolute";
				caption.style.top = `${imageContainerBottom + 20}px`;
				caption.style.left = "0";
				caption.style.width = "100%";
				caption.style.minHeight = "fit-content";

				// Update column-one height to account for caption
				setTimeout(() => {
					const captionRect = caption.getBoundingClientRect();
					const updatedColumnRect = columnOne.getBoundingClientRect();
					const captionBottom = captionRect.bottom - updatedColumnRect.top;
					const currentColumnHeight = columnOne.offsetHeight;
					if (captionBottom > currentColumnHeight) {
						columnOne.style.minHeight = `${captionBottom}px`;
					}
				}, 0);
			} else {
				// For desktop: keep caption inside image container
				// Move caption back inside if it was moved out
				const columnOneDesktop = imageContainer.closest(
					".staggered-media-and-content__column-one",
				);
				if (columnOneDesktop && caption.parentElement !== imageContainer) {
					imageContainer.appendChild(caption);
				}

				// Set image container to relative positioning if not already
				imageContainer.style.position = "relative";

				// Calculate image element's bottom position relative to image container
				const imageRect = img.getBoundingClientRect();
				const containerRect = imageContainer.getBoundingClientRect();
				const imageBottomY = imageRect.bottom - containerRect.top;

				caption.style.position = "absolute";
				caption.style.top = `${imageBottomY + 20}px`;
				caption.style.left = "0";
				caption.style.width = "100%";
				caption.style.height = "fit-content";

				// Update column-one height to account for caption on desktop
				if (columnOneDesktop) {
					setTimeout(() => {
						const captionRect = caption.getBoundingClientRect();
						const columnRect = columnOneDesktop.getBoundingClientRect();
						const captionBottom = captionRect.bottom - columnRect.top;
						const currentColumnHeight = columnOneDesktop.offsetHeight;
						if (captionBottom > currentColumnHeight) {
							columnOneDesktop.style.minHeight = `${captionBottom}px`;
						}
					}, 0);
				}
			}

			// Fade in up animation after position is calculated
			if (isInitial && typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined" && !caption.dataset.animated) {
				// Set initial state
				gsap.set(caption, {
					opacity: 0,
					y: 30,
				});
				
				// Create fade-in-up animation with ScrollTrigger
				var tl = gsap.timeline({
					scrollTrigger: {
						trigger: caption,
						start: "top 99%",
						end: "bottom 10%",
						toggleActions: "play none none none",
					},
				});
				
				tl.to(caption, {
					duration: 1,
					opacity: 1,
					y: 0,
					ease: "power2.out",
				});
				
				// Refresh ScrollTrigger after positioning is complete (longer delay for mobile DOM manipulation)
				setTimeout(() => {
					if (typeof ScrollTrigger !== "undefined") {
						ScrollTrigger.refresh();
					}
				}, isMobile ? 200 : 100);
				
				// Mark as animated to prevent re-animating
				caption.dataset.animated = "true";
			}
		}
	};

	// Initial positioning with fade-in animation
	imageContainers.forEach((imageContainer) => {
		updateCaptionPosition(imageContainer, true);
	});

	// Update on scroll and resize
	const updateAllCaptions = () => {
		imageContainers.forEach((imageContainer) => {
			updateCaptionPosition(imageContainer);
		});
	};

	// Use ScrollTrigger to update on scroll
	if (typeof ScrollTrigger !== "undefined") {
		ScrollTrigger.addEventListener("refresh", updateAllCaptions);
	}

	// Also listen to window resize
	window.addEventListener("resize", updateAllCaptions);

	// Use requestAnimationFrame for smooth constant updates during scroll
	let ticking = false;
	const onScroll = () => {
		if (!ticking) {
			window.requestAnimationFrame(() => {
				updateAllCaptions();
				ticking = false;
			});
			ticking = true;
		}
	};

	window.addEventListener("scroll", onScroll, { passive: true });
	
	// Also update constantly using requestAnimationFrame loop
	const constantUpdate = () => {
		updateAllCaptions();
		requestAnimationFrame(constantUpdate);
	};
	requestAnimationFrame(constantUpdate);
};

// initialize gsap animations
const gsapMediaAndContentAnimations = () => {
	const wrappers = document.querySelectorAll(
		".wp-block-create-block-staggered-media-and-content",
	);

	const stickyImage = (wrapper) => {
		const image = wrapper.querySelector(".staggered-media-and-content__image");
		const video = wrapper.querySelector(".staggered-media-and-content__video");
		const backgroundImage = wrapper.querySelector(
			".staggered-media-and-content__background-image",
		);

		if (wrapper && (image || video || backgroundImage)) {
			// Create ScrollTrigger instance to pin the image
			ScrollTrigger.create({
				trigger: wrapper,
				start: "top top",
				end: "bottom bottom",
				pin: image || video,
				pinSpacing: false,
				onUpdate: () => {
					// Update caption position when scroll trigger updates using Y position
					const imageContainer = wrapper.querySelector(
						".staggered-media-and-content__image",
					);
					if (imageContainer) {
						const columnOne = imageContainer.closest(
							".staggered-media-and-content__column-one",
						);
						if (!columnOne) return;

						// Find caption - could be inside image container or as sibling on mobile
						const caption = imageContainer.querySelector(
							".staggered-media-and-content__caption",
						) || columnOne.querySelector(
							".staggered-media-and-content__caption",
						);
						const img = imageContainer.querySelector("img");
						if (caption && img) {
							const TABLET_MAX = 1025; // Match $tablet-max breakpoint
							const isMobile = window.innerWidth < TABLET_MAX;

							if (isMobile) {
								// For mobile/tablet: position based on image container relative to column-one
								const imageContainerRect = imageContainer.getBoundingClientRect();
								const columnRect = columnOne.getBoundingClientRect();
								const imageContainerBottom = imageContainerRect.bottom - columnRect.top;
								const imageHeight = imageContainerRect.height;
								
								// Position at image bottom - 40% of image height
								const topPosition = imageContainerBottom - (imageHeight * 0.4);
								caption.style.top = `${topPosition}px`;
								caption.style.minHeight = "fit-content";

								// Update column-one height to account for caption
								setTimeout(() => {
									const captionRect = caption.getBoundingClientRect();
									const updatedColumnRect = columnOne.getBoundingClientRect();
									const captionBottom = captionRect.bottom - updatedColumnRect.top;
									const currentColumnHeight = columnOne.offsetHeight;
									if (captionBottom > currentColumnHeight) {
										columnOne.style.minHeight = `${captionBottom}px`;
									}
								}, 0);
							} else {
								// For desktop: position at image bottom Y + 20px
								const imageRect = img.getBoundingClientRect();
								const containerRect = imageContainer.getBoundingClientRect();
								const imageBottomY = imageRect.bottom - containerRect.top;
								caption.style.top = `${imageBottomY + 20}px`;

								// Update column-one height to account for caption
								setTimeout(() => {
									const captionRect = caption.getBoundingClientRect();
									const updatedColumnRect = columnOne.getBoundingClientRect();
									const captionBottom = captionRect.bottom - updatedColumnRect.top;
									const currentColumnHeight = columnOne.offsetHeight;
									if (captionBottom > currentColumnHeight) {
										columnOne.style.minHeight = `${captionBottom}px`;
									}
								}, 0);
							}
						}
					}
				},
			});

			// Add parallax scrolling effect to background image
			if (backgroundImage) {
				gsap.set(backgroundImage, {
					y: "40%",
				});

				gsap.to(backgroundImage, {
					y: "-10%",
					ease: "none",
					scrollTrigger: {
						trigger: wrapper,
						start: "top 100%",
						end: "bottom 105%",
						scrub: true,
					},
				});
			}
		}
	};

	// media query to prevent animations from running on wrong different screen sizes
	let mm = gsap.matchMedia();

	const breakPoint = 1025;
	
	// Desktop: sticky image animation
	mm.add(
		{
			isDesktop: `(min-width: ${breakPoint}px)`,
			isMobile: `(max-width: ${breakPoint - 1}px)`,
		},
		(context) => {
			if (context.conditions.isDesktop) {
				wrappers.forEach((wrapper) => {
					stickyImage(wrapper);
				});
			} else if (context.conditions.isMobile) {
				parallaxImages(wrappers);
			}
		},
	);
};

// Main initialization function
const initStaggeredMediaAndContent = () => {
	// Run all initialization functions in correct order
	handleIframeVideos();
	handleLocalVideos();
	positionCaptions(); // Run BEFORE GSAP animations
	gsapMediaAndContentAnimations();
};

// Initialize based on document ready state
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initStaggeredMediaAndContent);
} else {
	// Document already loaded, initialize immediately with slight delay
	setTimeout(initStaggeredMediaAndContent, 0);
}